import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import comptabiliteService, {
  Ecriture, JournalComptable, CompteComptable,
} from '../../api/comptabilite';
import { formatTND, formatDate } from '../../utils/formatters';

const startOfYear = () => `${new Date().getFullYear()}-01-01`;
const today = () => new Date().toISOString().slice(0, 10);

interface DraftLine {
  compteNumero: string;
  libelle: string;
  debit: string;
  credit: string;
}
const emptyLine = (): DraftLine => ({ compteNumero: '', libelle: '', debit: '', credit: '' });

const Ecritures: React.FC = () => {
  const [startDate, setStartDate] = useState(startOfYear());
  const [endDate, setEndDate] = useState(today());
  const [ecritures, setEcritures] = useState<Ecriture[]>([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState<number | null>(null);

  // Manual entry
  const [showModal, setShowModal] = useState(false);
  const [journaux, setJournaux] = useState<JournalComptable[]>([]);
  const [comptes, setComptes] = useState<CompteComptable[]>([]);
  const [draft, setDraft] = useState({
    journalCode: 'OD',
    dateEcriture: today(),
    libelle: '',
    reference: '',
    lignes: [emptyLine(), emptyLine()],
  });

  const load = async () => {
    setLoading(true);
    try {
      setEcritures(await comptabiliteService.listEcritures(startDate, endDate));
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [startDate, endDate]);

  const openModal = async () => {
    setShowModal(true);
    if (journaux.length === 0 || comptes.length === 0) {
      try {
        const [j, c] = await Promise.all([
          comptabiliteService.listJournaux(),
          comptabiliteService.listComptes(),
        ]);
        setJournaux(j);
        setComptes(c);
      } catch (e: any) {
        toast.error('Impossible de charger journaux/comptes');
      }
    }
  };

  const totals = useMemo(() => {
    const d = draft.lignes.reduce((s, l) => s + (Number(l.debit) || 0), 0);
    const c = draft.lignes.reduce((s, l) => s + (Number(l.credit) || 0), 0);
    return { debit: d, credit: c, balanced: Math.abs(d - c) < 0.0005 && d > 0 };
  }, [draft.lignes]);

  const setLine = (i: number, patch: Partial<DraftLine>) =>
    setDraft((prev) => ({
      ...prev,
      lignes: prev.lignes.map((l, idx) => (idx === i ? { ...l, ...patch } : l)),
    }));

  const submit = async () => {
    if (!draft.libelle.trim()) { toast.error('Libellé requis'); return; }
    const lignes = draft.lignes
      .filter((l) => l.compteNumero && (Number(l.debit) > 0 || Number(l.credit) > 0))
      .map((l) => ({
        compteNumero: l.compteNumero,
        libelle: l.libelle || undefined,
        debit: Number(l.debit) || 0,
        credit: Number(l.credit) || 0,
      }));
    if (lignes.length < 2) { toast.error('Au moins deux lignes mouvementées'); return; }
    if (!totals.balanced) { toast.error('Écriture déséquilibrée'); return; }
    try {
      await comptabiliteService.createEcriture({
        journalCode: draft.journalCode,
        dateEcriture: draft.dateEcriture,
        libelle: draft.libelle,
        reference: draft.reference || undefined,
        lignes,
      });
      toast.success('Écriture créée');
      setShowModal(false);
      setDraft({ journalCode: 'OD', dateEcriture: today(), libelle: '', reference: '', lignes: [emptyLine(), emptyLine()] });
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erreur');
    }
  };

  const valider = async (e: Ecriture) => {
    try {
      await comptabiliteService.validerEcriture(e.id);
      toast.success(`Écriture ${e.numero} validée`);
      load();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Erreur');
    }
  };

  const supprimer = async (e: Ecriture) => {
    if (!window.confirm(`Supprimer l'écriture ${e.numero} ?`)) return;
    try {
      await comptabiliteService.deleteEcriture(e.id);
      toast.success('Écriture supprimée');
      load();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Erreur');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Journal des écritures</h1>
          <p className="text-sm text-gray-500">Écritures comptables générées et saisies manuellement.</p>
        </div>
        <button onClick={openModal}
                className="px-3 py-2 text-sm bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700">
          + Nouvelle écriture (OD)
        </button>
      </div>

      <div className="flex flex-wrap items-end gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Du</label>
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                 className="rounded-lg border border-gray-200 px-3 py-2" />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Au</label>
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                 className="rounded-lg border border-gray-200 px-3 py-2" />
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <th className="text-left px-4 py-2 font-medium">Date</th>
              <th className="text-left px-4 py-2 font-medium">Pièce</th>
              <th className="text-left px-4 py-2 font-medium">Journal</th>
              <th className="text-left px-4 py-2 font-medium">Libellé</th>
              <th className="text-left px-4 py-2 font-medium">Source</th>
              <th className="text-right px-4 py-2 font-medium">Débit</th>
              <th className="text-right px-4 py-2 font-medium">Crédit</th>
              <th className="text-left px-4 py-2 font-medium">Statut</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="px-4 py-6 text-center text-gray-400">Chargement…</td></tr>
            ) : ecritures.length === 0 ? (
              <tr><td colSpan={9} className="px-4 py-6 text-center text-gray-400">
                Aucune écriture. Générez-les depuis l'accueil Comptabilité ou créez une écriture manuelle.
              </td></tr>
            ) : ecritures.map((e) => (
              <React.Fragment key={e.id}>
                <tr className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer"
                    onClick={() => setExpanded(expanded === e.id ? null : e.id)}>
                  <td className="px-4 py-2 whitespace-nowrap">{formatDate(e.dateEcriture)}</td>
                  <td className="px-4 py-2 font-mono">{e.numero}</td>
                  <td className="px-4 py-2 font-mono">{e.journalCode}</td>
                  <td className="px-4 py-2">{e.libelle}</td>
                  <td className="px-4 py-2"><span className="text-xs text-gray-500">{e.sourceType}</span></td>
                  <td className="px-4 py-2 text-right">{formatTND(e.totalDebit)}</td>
                  <td className="px-4 py-2 text-right">{formatTND(e.totalCredit)}</td>
                  <td className="px-4 py-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      e.statut === 'VALIDEE' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                    }`}>
                      {e.statut === 'VALIDEE' ? 'Validée' : 'Brouillon'}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right whitespace-nowrap" onClick={(ev) => ev.stopPropagation()}>
                    {e.statut === 'BROUILLON' && (
                      <>
                        <button onClick={() => valider(e)} className="text-green-700 hover:underline text-xs mr-3">
                          Valider
                        </button>
                        <button onClick={() => supprimer(e)} className="text-red-600 hover:underline text-xs">
                          Supprimer
                        </button>
                      </>
                    )}
                  </td>
                </tr>
                {expanded === e.id && (
                  <tr className="bg-gray-50">
                    <td colSpan={9} className="px-4 py-3">
                      <table className="w-full text-xs">
                        <thead className="text-gray-500">
                          <tr>
                            <th className="text-left py-1">Compte</th>
                            <th className="text-left py-1">Libellé</th>
                            <th className="text-right py-1">Débit</th>
                            <th className="text-right py-1">Crédit</th>
                          </tr>
                        </thead>
                        <tbody>
                          {e.lignes.map((l, i) => (
                            <tr key={i}>
                              <td className="py-1 font-mono">{l.compteNumero} {l.compteLibelle ? `— ${l.compteLibelle}` : ''}</td>
                              <td className="py-1">{l.libelle}</td>
                              <td className="py-1 text-right">{l.debit ? formatTND(l.debit) : ''}</td>
                              <td className="py-1 text-right">{l.credit ? formatTND(l.credit) : ''}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Manual entry modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
             onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-xl w-full max-w-3xl max-h-[90vh] overflow-auto p-6 space-y-4"
               onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-semibold">Nouvelle écriture</h2>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1">Journal</label>
                <select value={draft.journalCode} onChange={(e) => setDraft({ ...draft, journalCode: e.target.value })}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2">
                  {(journaux.length ? journaux.map((j) => j.code) : ['OD']).map((code) => (
                    <option key={code} value={code}>{code}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Date</label>
                <input type="date" value={draft.dateEcriture}
                       onChange={(e) => setDraft({ ...draft, dateEcriture: e.target.value })}
                       className="w-full rounded-lg border border-gray-200 px-3 py-2" />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-medium mb-1">Libellé</label>
                <input value={draft.libelle} onChange={(e) => setDraft({ ...draft, libelle: e.target.value })}
                       className="w-full rounded-lg border border-gray-200 px-3 py-2" />
              </div>
            </div>

            <div className="space-y-2">
              {draft.lignes.map((l, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center">
                  <select value={l.compteNumero} onChange={(e) => setLine(i, { compteNumero: e.target.value })}
                          className="col-span-4 rounded-lg border border-gray-200 px-2 py-2 text-sm">
                    <option value="">— compte —</option>
                    {comptes.map((c) => <option key={c.id} value={c.numero}>{c.numero} — {c.libelle}</option>)}
                  </select>
                  <input value={l.libelle} onChange={(e) => setLine(i, { libelle: e.target.value })}
                         placeholder="Libellé" className="col-span-3 rounded-lg border border-gray-200 px-2 py-2 text-sm" />
                  <input type="number" value={l.debit} onChange={(e) => setLine(i, { debit: e.target.value, credit: '' })}
                         placeholder="Débit" className="col-span-2 rounded-lg border border-gray-200 px-2 py-2 text-sm text-right" />
                  <input type="number" value={l.credit} onChange={(e) => setLine(i, { credit: e.target.value, debit: '' })}
                         placeholder="Crédit" className="col-span-2 rounded-lg border border-gray-200 px-2 py-2 text-sm text-right" />
                  <button onClick={() => setDraft({ ...draft, lignes: draft.lignes.filter((_, idx) => idx !== i) })}
                          className="col-span-1 text-red-500 text-lg" title="Retirer">×</button>
                </div>
              ))}
              <button onClick={() => setDraft({ ...draft, lignes: [...draft.lignes, emptyLine()] })}
                      className="text-sm text-blue-600 hover:underline">+ Ajouter une ligne</button>
            </div>

            <div className="flex items-center justify-between border-t border-gray-100 pt-3 text-sm">
              <div className="space-x-4">
                <span>Total débit : <strong>{formatTND(totals.debit)}</strong></span>
                <span>Total crédit : <strong>{formatTND(totals.credit)}</strong></span>
              </div>
              <span className={totals.balanced ? 'text-green-600' : 'text-red-600'}>
                {totals.balanced ? 'Équilibrée' : 'Déséquilibrée'}
              </span>
            </div>

            <div className="flex justify-end gap-2">
              <button onClick={() => setShowModal(false)}
                      className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50">Annuler</button>
              <button onClick={submit} disabled={!totals.balanced}
                      className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50">
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Ecritures;
